import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { xor, capitalize, omit } from 'lodash';
import { ResponsiveImage, CollapsibleMenu } from '@web/molecules';
import { ButtonLink } from '@web/atoms';
import {
  IngredientModal,
  useIngredientModal,
  ProductSelector,
  SplitScreenPromo,
  MealPrepModal,
} from '@web/components';
import {
  userViewMealPlanTypeScreen,
  userViewPlanDetailsPage,
} from '@web/account/MealPrepTypeSelectionForm/events';
import { useShopifyCart } from '@web/shopify';
import { CheckoutBar } from '../CheckoutBar';
import freshPackPhoto from '../../images/onboarding/collapsible-menu-fresh-packs.png';
import bakedPackPhoto from '../../images/onboarding/collapsible-menu-baked-packs.png';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import {
  userContinuesFromRecommendedPlan,
  recipeSelected,
  recipeUnselected,
  userExpandsFreshRecipeDetails,
  userExpandsMealPlanDetails,
  userExpandsCollapsibleMenu,
  mealPlanTypeSelected,
} from './events';
import {
  StyledRecommendedPlanContainer,
  StyledForm,
  HeaderContainer,
  FullHeadline,
  FullSubheadline,
  ProductCardContainer,
  PlanContainer,
  BackgroundImage,
  SectionHeader,
  MenuText,
  MenuImageWrapper,
  SelectionText,
  CollapsibleMenuTitleSection,
  CollapsibleMenuWrapper,
} from './styles';

export const RecommendedMealPlanForm = ({
  onSubmit,
  headline,
  subheadline,
  modals,
  ctaText,
  freshRecipes,
  defaultFreshRecipes,
  bakedRecipes,
  defaultBakedRecipes,
  defaultMealPrepType,
  mealPrepTypes,
  unavailableMealPrepTypes,
  collapsedMealPrepTypes,
  onBackButtonClick,
  unavailableFreshRecipes,
  unavailableBakedRecipes,
  period,
  getConfigFromRecipes,
  splitScreenPromoProps,
  headerImage,
  modalContent,
  notSelectableText,
  hideHalfFresh = false,
  showFreshOnly = false,
  hideMixed = false,
  ctaButtonVariant = 'spinach',
  isWithinOptimized = false,
}) => {
  const availableModalContent = modalContent.filter(
    ({ value }) => !unavailableMealPrepTypes?.includes(value),
  );

  const [selectedMealPrepType, setSelectedMealPrepType] =
    useState(defaultMealPrepType);
  const [selectedFreshRecipes, setSelectedFreshRecipes] =
    useState(defaultFreshRecipes);
  const [selectedBakedRecipes, setSelectedBakedRecipes] =
    useState(defaultBakedRecipes);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const viewEventsFired = useRef(false);

  const reporter = useReporter();

  const { customer, isAppSession } = useSessionCustomer();
  const { cart } = useShopifyCart();

  const { modal, onRecipeDetailsClick } = useIngredientModal(modals);

  useEffect(() => {
    if (!viewEventsFired.current) {
      if (isWithinOptimized) {
        reporter.tag(userViewPlanDetailsPage());
      } else {
        reporter.tag(userViewMealPlanTypeScreen());
      }
      viewEventsFired.current = true;
    }
    if (
      selectedMealPrepType === 'fresh' ||
      selectedMealPrepType === 'half_fresh'
    ) {
      setSelectedBakedRecipes([]);
    } else {
      setSelectedBakedRecipes(defaultBakedRecipes);
    }
  }, [
    reporter,
    cart,
    selectedMealPrepType,
    defaultBakedRecipes,
    isWithinOptimized,
  ]);

  const onFreshChange = freshRecipes => {
    const currentRecipes = selectedFreshRecipes ?? [];
    const recipeAdded = freshRecipes.length > currentRecipes.length;
    const recipe = xor(freshRecipes, currentRecipes)[0];
    const event = recipeAdded ? recipeSelected : recipeUnselected;
    reporter.tag(event(recipe));
    setSelectedFreshRecipes(freshRecipes);
  };

  const onBakedChange = bakedRecipes => {
    const currentRecipes = selectedBakedRecipes ?? [];
    const recipeAdded = bakedRecipes.length > currentRecipes.length;
    const recipe = xor(bakedRecipes, currentRecipes)[0];
    const event = recipeAdded ? recipeSelected : recipeUnselected;
    reporter.tag(event(recipe));
    setSelectedBakedRecipes(bakedRecipes);
  };

  const onMealPrepChange = product => {
    reporter.tag(mealPlanTypeSelected(product[0]));
    if (unavailableMealPrepTypes?.includes(product[0])) return null;
    setSelectedMealPrepType(product[0]);
  };

  const isValid =
    selectedMealPrepType === 'fresh' || selectedMealPrepType === 'half_fresh'
      ? selectedFreshRecipes && selectedFreshRecipes.length > 0
      : selectedFreshRecipes &&
        selectedFreshRecipes.length > 0 &&
        selectedBakedRecipes &&
        selectedBakedRecipes.length > 0;

  const onSubmitHandler = async event => {
    event.preventDefault();
    if (!isValid) {
      return;
    }
    const customerEmail = cart?.customer?.email || customer?.email;
    reporter.tag(
      userContinuesFromRecommendedPlan(
        customerEmail,
        selectedMealPrepType,
        selectedFreshRecipes,
        selectedBakedRecipes,
      ),
    );
    setIsSubmitting(true);
    await onSubmit(
      selectedMealPrepType,
      selectedFreshRecipes,
      selectedBakedRecipes,
    );
  };

  const onDetailsClick = () => {
    reporter.tag(userExpandsMealPlanDetails());
    setShowModal(true);
  };

  const onClickCollapsibleMenuButton = () => {
    reporter.tag(userExpandsCollapsibleMenu());
  };

  const { price, discountedPrice, message } = getConfigFromRecipes(
    selectedMealPrepType,
    selectedFreshRecipes,
    selectedBakedRecipes,
  );

  const checkoutBarProps = {
    ctaText,
    isSubmitDisabled: isSubmitting,
    isBackButtonDisabled: isSubmitting,
    shown: true,
    onBackButtonClick: onBackButtonClick,
    period,
    isSubmitting: isSubmitting,
    relativePosition: isAppSession,
    price,
    discountedPrice,
    message,
    buttonVariant: ctaButtonVariant,
  };

  const legendFresh = `Select up to ${freshRecipes.length >= 4 && '4'}`;
  const legendBaked = `Select up to ${freshRecipes.length >= 2 && '2'}`;

  const freshMenuText = (
    <MenuText direction="row">
      <MenuImageWrapper>
        <ResponsiveImage
          src={freshPackPhoto}
          alt={'Photo of Ollie Fresh Packs'}
        />
      </MenuImageWrapper>
      {selectedFreshRecipes.map(recipe => capitalize(recipe)).join(', ')}
    </MenuText>
  );

  const mixedMenuText = (
    <MenuText direction="column">
      <CollapsibleMenuTitleSection>
        <MenuImageWrapper>
          <ResponsiveImage
            src={freshPackPhoto}
            alt={'Photo of Ollie Fresh Packs'}
          />
        </MenuImageWrapper>
        <div>
          <span>Fresh:</span>{' '}
          {selectedFreshRecipes.map(recipe => capitalize(recipe)).join(', ')}
        </div>
      </CollapsibleMenuTitleSection>
      <CollapsibleMenuTitleSection>
        <MenuImageWrapper>
          <ResponsiveImage
            src={bakedPackPhoto}
            alt={'Photo of Ollie Baked Packs'}
          />
        </MenuImageWrapper>
        <div>
          <span>Baked:</span>{' '}
          {selectedBakedRecipes.map(recipe => capitalize(recipe)).join(', ')}
        </div>
      </CollapsibleMenuTitleSection>
    </MenuText>
  );

  const onRecipeDetailsHandler = product => {
    reporter.tag(userExpandsFreshRecipeDetails(product.value));
    if (product?.title?.includes('Baked')) {
      onRecipeDetailsClick('baked_' + product.value);
    } else {
      onRecipeDetailsClick(product.value);
    }
  };

  const productSelectorProps = {
    onDetailsClick: onRecipeDetailsHandler,
    productCardVariant: 'recommended',
    direction: 'vertical',
    period,
    buttonIcon: 'plus',
  };

  //TODO: The problem here is that the planTypes being passed in are not valid. The array may be empty or undefined
  //TODO: Need to figure out why that is happening

  const getConfiguredPlanTypes = planTypes =>
    planTypes?.map(planType => ({
      ...planType,
      shouldShowMoreDetails: false,
      inputType: 'radio',
    }));

  const mealPrepSelectorProps = {
    selected: [selectedMealPrepType],
    unavailableProducts: unavailableMealPrepTypes,
    variant: 'radio',
    direction: 'horizontal',
    cardOrientation: 'column',
    notSelectableText: notSelectableText,
  };

  return (
    <StyledForm onSubmit={onSubmitHandler}>
      <StyledRecommendedPlanContainer>
        <MealPrepModal
          mealPrepTypes={availableModalContent}
          shown={showModal}
          onClickClose={() => setShowModal(false)}
        ></MealPrepModal>
        <>
          <BackgroundImage>
            <ResponsiveImage
              {...omit(headerImage, ['width', 'height'])}
              fill={true}
              objectFit="cover"
            />
          </BackgroundImage>
          <HeaderContainer>
            <FullHeadline>{headline}</FullHeadline>
            <FullSubheadline>
              {subheadline}{' '}
              {!showFreshOnly && !hideMixed && (
                <ButtonLink variant="text.lightest" onClick={onDetailsClick}>
                  See details
                </ButtonLink>
              )}
            </FullSubheadline>
            {showFreshOnly || hideMixed ? (
              <ProductSelector
                gridColumns={1}
                stackOnMobile={false}
                products={getConfiguredPlanTypes(mealPrepTypes)}
                unavailableProducts={unavailableMealPrepTypes}
                onChange={onMealPrepChange}
                productCardVariant="cro"
                showPricePerWeek={false}
                {...mealPrepSelectorProps}
              />
            ) : (
              <ProductSelector
                gridColumns={2}
                stackOnMobile={false}
                products={getConfiguredPlanTypes(mealPrepTypes)}
                unavailableProducts={unavailableMealPrepTypes}
                onChange={onMealPrepChange}
                productCardVariant="recommended"
                {...mealPrepSelectorProps}
              />
            )}
          </HeaderContainer>
          <ProductCardContainer>
            <SectionHeader>Your Recipes</SectionHeader>
            <fieldset>
              <CollapsibleMenuWrapper>
                {selectedMealPrepType === 'fresh' ||
                selectedMealPrepType === 'half_fresh' ? (
                  <CollapsibleMenu
                    menuText={freshMenuText}
                    onClickMenuButton={onClickCollapsibleMenuButton}
                  >
                    <SelectionText>{legendFresh}</SelectionText>
                    <ProductSelector
                      gapSize={2}
                      products={freshRecipes}
                      selected={selectedFreshRecipes}
                      unavailableProducts={unavailableFreshRecipes}
                      {...productSelectorProps}
                      onChange={onFreshChange}
                    />
                  </CollapsibleMenu>
                ) : (
                  <CollapsibleMenu
                    menuText={mixedMenuText}
                    maxHeight="121px"
                    onClickMenuButton={onClickCollapsibleMenuButton}
                  >
                    <SelectionText>{legendFresh}</SelectionText>
                    <ProductSelector
                      gapSize={2}
                      products={freshRecipes}
                      selected={selectedFreshRecipes}
                      unavailableProducts={unavailableFreshRecipes}
                      {...productSelectorProps}
                      onChange={onFreshChange}
                    />
                    <SelectionText>{legendBaked}</SelectionText>
                    <ProductSelector
                      gapSize={2}
                      products={bakedRecipes}
                      selected={selectedBakedRecipes}
                      unavailableProducts={unavailableBakedRecipes}
                      {...productSelectorProps}
                      onChange={onBakedChange}
                    />
                  </CollapsibleMenu>
                )}
              </CollapsibleMenuWrapper>
            </fieldset>

            <IngredientModal {...modal} />
          </ProductCardContainer>
        </>
        {(selectedMealPrepType === 'fresh' ||
          selectedMealPrepType === 'half_fresh') &&
          !hideHalfFresh &&
          !showFreshOnly && (
            <PlanContainer>
              <SectionHeader>Looking to try us out for less?</SectionHeader>
              <ProductSelector
                products={getConfiguredPlanTypes(collapsedMealPrepTypes)}
                unavailableProducts={unavailableMealPrepTypes}
                variant="radio"
                productCardVariant="cro"
                direction="horizontal"
                onChange={onMealPrepChange}
                {...mealPrepSelectorProps}
              />
            </PlanContainer>
          )}
      </StyledRecommendedPlanContainer>
      <SplitScreenPromo {...splitScreenPromoProps} />
      <CheckoutBar {...checkoutBarProps} />
    </StyledForm>
  );
};
RecommendedMealPlanForm.displayName = 'RecommendedMealPlanForm';
RecommendedMealPlanForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  headline: PropTypes.string,
  subheadline: PropTypes.string,
  modals: PropTypes.array,
  ctaText: PropTypes.string.isRequired,
  freshRecipes: PropTypes.array,
  defaultFreshRecipes: PropTypes.array,
  bakedRecipes: PropTypes.array,
  defaultBakedRecipes: PropTypes.array,
  mealPrepTypes: PropTypes.array,
  defaultMealPrepTypes: PropTypes.array,
  onBackButtonClick: PropTypes.func.isRequired,
  unavailableFreshRecipes: PropTypes.array,
  unavailableBakedRecipes: PropTypes.array,
  unavailableMealPrepTypes: PropTypes.array,
  collapsedMealPrepTypes: PropTypes.array,
  period: PropTypes.string.isRequired,
  headerImage: PropTypes.object,
  splitScreenPromoProps: PropTypes.object,
  modelContent: PropTypes.array,
  getConfigFromRecipes: PropTypes.func.isRequired,
  notSelectableText: PropTypes.string,
  hideHalfFresh: PropTypes.bool,
  showFreshOnly: PropTypes.bool,
  hideMixed: PropTypes.bool,
};
